import { LucideIcon } from "lucide-react";
import { appointment_status } from "@/models/appointment.model";
import { uuid } from "@/interfaces";

export enum UserRole {
  PMC = "pmc",
  VENDOR = "vendor",
  SUBS = "subs",
}

export enum AppointmentActionCategory {
  STATUS = "status",
  SCHEDULING = "scheduling",
  ASSIGNMENT = "assignment",
  SERVICE = "service",
  COMMUNICATION = "communication",
  REQUEST = "request",
}

export interface AppointmentActionContext {
  appointmentId: uuid;
  currentStatus: appointment_status;
  userRole: UserRole;
  vendorId?: uuid;
  serviceId?: uuid;
  currentDate?: string;
  roomId?: uuid;
}

export interface AppointmentActionResult {
  success: boolean;
  message?: string;
  data?: any;
  requiresApproval?: boolean;
  approvalMessageId?: uuid;
}

export interface AppointmentActionPayload {
  [key: string]: any;
}

export interface AppointmentActionPermission {
  requiredRole?: UserRole[];
  requiredStatuses?: appointment_status[];
  excludedStatuses?: appointment_status[];
}

export interface AppointmentActionConfig {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  category: AppointmentActionCategory;
  permissions: AppointmentActionPermission;
  variant?: "default" | "destructive" | "secondary" | "outline";
  requiresDialog?: boolean;
}

export type AppointmentMenuEntry = { readonly config: AppointmentActionConfig };

export function isOffered({ permissions }: AppointmentActionConfig, { userRole, currentStatus }: Pick<AppointmentActionContext, "userRole" | "currentStatus">): boolean {
  if (permissions.requiredRole && !permissions.requiredRole.includes(userRole)) return false;
  if (permissions.requiredStatuses && !permissions.requiredStatuses.includes(currentStatus)) return false;
  if (permissions.excludedStatuses && permissions.excludedStatuses.includes(currentStatus)) return false;
  return true;
}

export abstract class BaseAppointmentAction implements AppointmentMenuEntry {
  abstract readonly config: AppointmentActionConfig;

  isAvailable(context: AppointmentActionContext): boolean {
    return isOffered(this.config, context);
  }

  /**
   * Execute the action with the given context and payload
   */
  abstract execute(
    context: AppointmentActionContext,
    payload?: AppointmentActionPayload
  ): Promise<AppointmentActionResult>;

  /**
   * Validate the payload for this action (optional override)
   */
  validatePayload(payload?: AppointmentActionPayload): string | null {
    return null; // No validation by default
  }

  /**
   * Get the dialog component name for this action (if applicable)
   */
  getDialogComponent(): string | null {
    return null; // No dialog by default
  }
}
